import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Checkbox from '../checkbox';

const Disabled = css`
  cursor: not-allowed;
  opacity: 0.3 !important;
`;

const PanelWrapper = styled.div`
  background-color: #fff;
  border: 1px solid
    ${props =>
      props.isSelected
        ? props.theme.carvana.blue
        : props.theme.carvana.lightGray};
  border-radius: 4px;
  position: relative;
  width: 100%;

  ${props => props.disabled && Disabled}
`;

const PanelHeader = styled.div`
  border-bottom: 1px solid
    ${props =>
      props.isSelected
        ? props.theme.carvana.blue
        : props.theme.carvana.lightGray};
  cursor: pointer;
  display: flex;
  padding: 1rem;
`;

const PanelHeaderValueRight = styled.span`
  color: ${props => props.theme.carvana.blue};
  display: inline-flex;
  font-size: 1.2rem;
  font-weight: bold;
  margin-left: auto;
  margin-right: 0;
`;

const PanelHeaderValueRightBanner = styled.span`
  color: ${props => props.theme.carvana.blue};
  display: inline-flex;
  font-size: 1.2rem;
  font-weight: bold;
  margin-left: auto;
  margin-right: 0;
  padding-right: 1.25rem;
`;

const PanelBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  height: 84%;
`;

const formatPanelHeaderValue = value => {
  const absValue = Math.abs(value);

  return value >= 0 ? `+ $${absValue}` : `- $${absValue}`;
};

const PanelCheckbox = ({
  onChange,
  name,
  checkboxValue,
  checkboxText,
  children,
  className,
  disabled,
  panelBodyStyles,
  panelHeaderStyles,
  panelHeaderValueRight,
  panelHeaderTextRight,
  panelRightHeaderImage,
  showRightPanelHeader
}) => (
  <PanelWrapper className={className} isSelected={checkboxValue}>
    <PanelHeader
      css={panelHeaderStyles}
      isSelected={checkboxValue}
      onClick={() => onChange(name)}
    >
      <Checkbox
        disabled={disabled}
        round
        text={checkboxText || (checkboxValue ? 'Selected' : 'Select')}
        value={checkboxValue}
        name={name}
        onChange={() => onChange(name)}
        color="greenTwo"
        data-cv-test={`${name}Selection`}
      />
      {panelHeaderValueRight !== null && (
        <PanelHeaderValueRight data-cv-test={`${name}Value`}>
          {formatPanelHeaderValue(panelHeaderValueRight)}
          {panelHeaderTextRight}
        </PanelHeaderValueRight>
      )}

      {panelRightHeaderImage && showRightPanelHeader && (
        <PanelHeaderValueRightBanner>
          {typeof panelRightHeaderImage === 'function'
            ? panelRightHeaderImage({ isSelected: checkboxValue, name })
            : panelRightHeaderImage}
        </PanelHeaderValueRightBanner>
      )}
    </PanelHeader>

    <PanelBody isSelected={checkboxValue} css={panelBodyStyles}>
      {typeof children === 'function'
        ? children({ isSelected: checkboxValue, name })
        : children}
    </PanelBody>
  </PanelWrapper>
);

PanelCheckbox.propTypes = {
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  disabled: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  checkboxText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  checkboxValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  panelBodyStyles: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  panelHeaderStyles: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  panelHeaderValueRight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  panelHeaderTextRight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ]),
  panelRightHeaderImage: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  showRightPanelHeader: PropTypes.bool
};

PanelCheckbox.defaultProps = {
  panelBodyStyles: [],
  panelHeaderStyles: [],
  panelHeaderValueRight: null,
  panelHeaderTextRight: '',
  children: null,
  disabled: false,
  className: null,
  checkboxText: null,
  checkboxValue: null,
  panelRightHeaderImage: null,
  showRightPanelHeader: false
};

export default PanelCheckbox;
